import React, { useEffect, useRef, useState, useMemo } from "react";
import { UseFormRegister, FieldErrors, Control, useWatch, Controller } from "react-hook-form";
import Input from "../../../components/FormInputs";
import CustomButton from "../../../components/AddButton";
import FormTitle from "../../../components/FormTitle";
import CustomModal from "../../../components/Modal";
import { MASTER_URL } from "../../../common/common";
import { ApiService } from "../../../service/api.service";
import CustomDropDown from "../../../components/Dropdown/CustomDropDown";
import { Plus,cancelButton } from "../../../common/svg";
import SecureImage from "../../../components/BucketImage/Image";
// Define interfaces for our data structure
interface ContainerDetail {
  containerType: string;
  containerSize: string;
  noOfContainer: number;
  containerSpecification: string;
  remarks: string;
  [key: string]: any; // Allow for other dynamic properties
}

interface ContainerDetailsProps {
  errors: FieldErrors;
  register: UseFormRegister<any>;
  setValue: any;
  control: Control<any>;
  setContainerDetailsData: (data: any) => void;
  ContainerDetailsData?: any[];
}
const ContainerDetails: React.FC<ContainerDetailsProps> = ({
  errors,
  register,
  setValue,
  control,
  setContainerDetailsData,
  ContainerDetailsData
}) => {
  const apiService = new ApiService();
  const [forms, setForms] = useState<number[]>([0]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [formIdToRemove, setFormIdToRemove] = useState<number | null>(null);
  const [containerTypes, setContainerTypes] = useState<any[]>([]);
  const [containerSubTypes, setContainerSubTypes] = useState<any[]>([]);
  const [containerSizes, setContainerSizes] = useState<any[]>([]);
  const isMasterDataFetched = useRef(false);
  const isInitialRender = useRef(true);

  const watchFields = useWatch({ control, name: "containerDetails" }) as ContainerDetail[] || [];

  // Memoize container details to prevent unnecessary re-renders
  const memoizedContainerDetails = useMemo(() => {
    return ContainerDetailsData || [];
  }, [ContainerDetailsData]);

  // Helper function to get field error with proper type handling
  const getFieldError = (index: number, fieldName: string): string => {
    const containerDetailsErrors = errors.containerDetails as Record<string, any> | undefined;
    return containerDetailsErrors?.[index]?.[fieldName]?.message || "";
  };

  // Helper function to check if field has error with proper type handling
  const hasFieldError = (index: number, fieldName: string): boolean => {
    const containerDetailsErrors = errors.containerDetails as Record<string, any> | undefined;
    return !!containerDetailsErrors?.[index]?.[fieldName];
  };

  const openModal = (formId: number) => {
    setFormIdToRemove(formId);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleAddForm = () => {
    setForms((prevForms) => [...prevForms, prevForms.length]);
  };

  const handleConfirmRemove = () => {
    if (formIdToRemove !== null) {
      // Update the form array
      setForms((prevForms) => prevForms.filter((id) => id !== formIdToRemove));
      
      // Update the form data by removing the item and reindexing
      setValue("containerDetails", 
        (watchFields)
          .filter((_: ContainerDetail, idx: number) => idx !== formIdToRemove)
      );
      
      setFormIdToRemove(null);
      closeModal();
    }
  };

  // Master data fetching
  const getMasterData = async () => {
    if (isMasterDataFetched.current) return;

    try {
      const response: any = await apiService.getApi(`${MASTER_URL}/booking/data-route?key=Booking Type`);

      if (response.status === "200") {
        if (response.body["Container Type"]) {
          setContainerTypes(response.body["Container Type"].map((item: any) => ({
            value: item.metaId,
            label: item.value,
          })));
        }

        if (response.body["Container Type"]) {
          setContainerSubTypes(response.body["Container Type"].map((item: any) => ({
            value: item.metaId,
            label: item.value,
          })));
        }

        if (response.body["Container Size"]) {
          setContainerSizes(response.body["Container Size"].map((item: any) => ({
            value: item.metaId,
            label: item.value,
          })));
        }

        isMasterDataFetched.current = true;
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    }
  };

  // Initial data loading and form setup
  useEffect(() => {
    getMasterData();

    if (memoizedContainerDetails.length > 0 && isInitialRender.current) {
      const formCount = memoizedContainerDetails.length;
      setForms(Array.from({ length: formCount }, (_, i) => i));

      // Batch update form values only on initial render
      memoizedContainerDetails.forEach((data: ContainerDetail, index: number) => {
        Object.entries(data).forEach(([key, value]) => {
          setValue(`containerDetails[${index}].${key}`, value);
        });
      });

      isInitialRender.current = false;
    } else if (forms.length === 0) {
      setForms([0]);
    }
  }, [memoizedContainerDetails, setValue]);

  // Update parent component with form data
  useEffect(() => {
    // Only update if the fields have meaningful data
    if (watchFields && watchFields.some(field => Object.keys(field).length > 0)) {
      setContainerDetailsData(watchFields);
    }
  }, [watchFields, setContainerDetailsData]);

  return (
    <div>
      <div className="bg-white p-4 rounded-[20px]">
        <div className="flex items-center justify-between">
          <FormTitle title="Container Details" />
          <CustomButton label="" onClick={handleAddForm} icon={Plus} />
        </div>
        <div className="">
          {forms.map((formId, index) => (
            <div key={formId} className="gap-4 border-b border-dashed">
              <div className="flex items-center justify-end mb-2 gap-4">
          <p className="text-[14px] font-[600]">#{(index + 1).toString().padStart(2, "0")}</p>
          {index >= 1 && (
            <div className="flex items-center justify-end">
              <button
                onClick={() => openModal(formId)}
                type="button"
                className="bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-all"
                aria-label="Remove"
              >
                        <SecureImage
              fileKey={cancelButton}
              alt="down arrow"
              width="15px"
            />
              </button>
            </div>
          )}
          </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Controller
                    name={`containerDetails[${index}].containerType`}
                    control={control}
                    render={({ field }) => (
                      <CustomDropDown
                        label="Container Type"
                        name={`containerDetails[${index}].containerType`}
                        options={containerTypes}
                        register={register}
                        errors={errors}
                        defaultValue={field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                 
                </div>
                <div>
                <Input
                      label="No of Containers"
                      name={`containerDetails[${index}].noOfContainer`}
                      errors={hasFieldError(index, "noOfContainer") ? 
                        { [`containerDetails[${index}].noOfContainer`]: { message: getFieldError(index, "noOfContainer") } } : {}}
                      placeholder="Enter No of Containers"
                      type="number"
                      register={register}
                    />
                </div>
                <div>
                  <Controller
                    name={`containerDetails[${index}].containerSize`}
                    control={control}
                    render={({ field }) => (
                      <CustomDropDown
                        label="Container Size"
                        name={`containerDetails[${index}].containerSize`}
                        options={containerSizes}
                        register={register}
                        errors={errors}
                        defaultValue={field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
              
                  <Input
                    label="Container Specifications"
                    name={`containerDetails[${index}].containerSpecification`}
                    errors={hasFieldError(index, "containerSpecification") ? 
                      { [`containerDetails[${index}].containerSpecification`]: { message: getFieldError(index, "containerSpecification") } } : {}}
                    placeholder="Enter Container Specifications"
                    register={register}
                    multiline
                    rows={2}
                  />
              
                  <Input
                    label="Remarks"
                    name={`containerDetails[${index}].remarks`}
                    errors={hasFieldError(index, "remarks") ? 
                      { [`containerDetails[${index}].remarks`]: { message: getFieldError(index, "remarks") } } : {}}
                    placeholder="Enter Remarks"
                    register={register}
                    multiline
                    rows={2}
                  />
               
              </div>
             
            </div>
          ))}
        </div>
      </div>
      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center">
            <p className="text-[18px] font-[600] text-[#4B4B4B]">
              Are you sure you want to remove this form?
            </p>
            <div className="mt-4 flex gap-4">
              <button
                onClick={handleConfirmRemove}
                type="button"
                className="bg-red-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                Yes
              </button>
              <button
                onClick={closeModal}
                type="button"
                className="bg-gray-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                No
              </button>
            </div>
          </div>
        }
        onClose={closeModal}
      />
    </div>
  );
};

export default ContainerDetails;